import React, { useRef, useState, useCallback } from 'react';
import ContextSidebar from './ContextSidebar';
import SmartEditor from './SmartEditor';
import AiAssistant from './AiAssistant';
import vaultService from '../../../services/vaultService';

const PlanCanvas = ({
    episode,
    user,
    db,
    isPortalActive,
    onTogglePortal,
    portalUrl,
    plan,
    onSavePlan,
    settings,
    onOpenVault,
    isSidebarCollapsed = false,
    onToggleSidebar,
    // Dropped artifacts props from StudioLayout
    droppedArtifacts = [],
    onArtifactDrop,
    onRemoveArtifact,
    onClearArtifacts
}) => {
    const editorRef = useRef(null);
    const [editorSelection, setEditorSelection] = useState(null);

    // Mock research data for now
    const mockResearch = [
        { title: 'Recent Funding', content: 'Raised $40M Series B led by Andreessen Horowitz to expand into enterprise markets.' },
        { title: 'Podcast Feature', content: 'Appeared on "Acquired" podcast discussing the early days of YC.' },
        { title: 'Viral Tweet', content: '"AI isn\'t coming for your job, it\'s coming for your drudgery." - 12k likes.' },
    ];

    // Check if we have real research in episode data, otherwise use mock if empty (or just show empty state)
    // For this Phase 1 test, let's show the Mock data so the user sees the UI potential
    const researchData = episode?.guestBio ? [{ title: 'Bio Summary', content: episode.guestBio }] : mockResearch;

    /**
     * Handle adding text snippet to vault
     */
    const handleAddToVault = useCallback(async (text) => {
        try {
            // Use "Unnamed" as default title - user can edit after saving
            const result = await vaultService.uploadTextSnippet(text, 'Unnamed', episode?.id);
            if (!result.success) {
                console.error('Failed to save to vault:', result.error);
            }
        } catch (err) {
            console.error('Failed to save to vault:', err);
        }
    }, [episode?.id]);

    /**
     * Handle artifact drop from vault to editor
     * Note: Content insertion is handled by SmartEditor's TipTap handleDrop.
     * This callback only tracks the artifact for UI purposes.
     */
    const handleArtifactDrop = useCallback((artifact) => {
        // Content insertion is already handled by SmartEditor's TipTap handleDrop
        // This callback is just for tracking/artifact management purposes

        // Call parent handler if provided for tracking
        if (onArtifactDrop) {
            onArtifactDrop(artifact);
        }
    }, [onArtifactDrop]);

    return (
        <div className="relative h-full flex flex-col md:flex-row bg-[#FAFAFC]">
            {/* AI Assistant - Top Right Overlay */}
            <AiAssistant
                context={{
                    guestName: episode?.founderName || episode?.guestName,
                    guestBio: episode?.guestBio,
                    topics: episode?.topics,
                    podcastName: settings?.podcastName,
                    selectedText: editorSelection
                }}
                onInsertContent={(content) => {
                    editorRef.current?.insertContent(content);
                }}
            />

            {/* Left Panel: Context (Collapsible on mobile?) */}
            <div className="flex-none lg:flex-none">
                <ContextSidebar
                    episode={episode}
                    research={researchData}
                    isPortalActive={isPortalActive}
                    onTogglePortal={onTogglePortal}
                    portalUrl={portalUrl}
                    isCollapsed={isSidebarCollapsed}
                    onToggleCollapse={onToggleSidebar}
                />
            </div>

            {/* Right Panel: Editor */}
            <div className="flex-1 min-w-0 h-full overflow-y-auto">
                <SmartEditor
                    ref={editorRef}
                    initialContent={plan}
                    onSave={onSavePlan}
                    onOpenVault={onOpenVault}
                    onAddVaultArtifact={handleAddToVault}
                    episodeId={episode?.id}
                    onSelectionChange={setEditorSelection}
                    droppedArtifacts={droppedArtifacts}
                    onArtifactDrop={handleArtifactDrop}
                    onRemoveArtifact={onRemoveArtifact}
                    onClearArtifacts={onClearArtifacts}
                />
            </div>
        </div>
    );
};

export default PlanCanvas;
